import React, { useEffect, useState } from 'react';
import { diocesesService, paroissesService } from '../services/api';
import { FiPlus, FiEdit, FiTrash, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { useForm } from 'react-hook-form';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';

const DiocesesPage = () => {
  const [dioceses, setDioceses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDiocese, setEditingDiocese] = useState(null);
  const [expandedDiocese, setExpandedDiocese] = useState(null); // ID du diocèse ouvert
  const [paroissesByDiocese, setParoissesByDiocese] = useState({}); // Cache des paroisses par diocèse
  const [loadingParoisses, setLoadingParoisses] = useState({}); // État de chargement par diocèse
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const fetchDioceses = async () => {
    const res = await diocesesService.getAll();
    setDioceses(res.data.data || []);
  };

  const fetchParoisses = async (dioceseId) => {
    if (paroissesByDiocese[dioceseId]) {
      // Déjà chargées, pas besoin de recharger
      return;
    }
    
    setLoadingParoisses(prev => ({ ...prev, [dioceseId]: true }));
    try {
      const res = await paroissesService.getAll({ diocese_id: dioceseId });
      setParoissesByDiocese(prev => ({
        ...prev,
        [dioceseId]: res.data.data || []
      }));
    } catch (error) {
      Swal.fire('Erreur', 'Erreur lors du chargement des paroisses', 'error');
    } finally {
      setLoadingParoisses(prev => ({ ...prev, [dioceseId]: false }));
    }
  };

  const toggleDiocese = (dioceseId) => {
    if (expandedDiocese === dioceseId) {
      setExpandedDiocese(null);
    } else {
      setExpandedDiocese(dioceseId);
      fetchParoisses(dioceseId);
    }
  };

  useEffect(() => {
    fetchDioceses();
  }, []);

  const onSubmit = async (data) => {
    try {
      if (editingDiocese) {
        await diocesesService.update(editingDiocese.id, data);
        Swal.fire('Succès', 'Diocèse modifié avec succès', 'success');
      } else {
        await diocesesService.create(data);
        Swal.fire('Succès', 'Diocèse ajouté avec succès', 'success');
      }
      setShowModal(false);
      setEditingDiocese(null);
      reset();
      fetchDioceses();
    } catch (error) {
      Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Diocèses</h1>
        <ConditionalButton
          entity="dioceses"
          permission="create"
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={() => {
            setEditingDiocese(null);
            reset();
            setShowModal(true);
          }}
        >
          <FiPlus />
          <span>Nouveau diocèse</span>
        </ConditionalButton>
      </div>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12"></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Diocèse</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {dioceses.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Aucun diocèse</td>
              </tr>
            ) : (
              dioceses.map((diocese) => {
                const isExpanded = expandedDiocese === diocese.id;
                const paroisses = paroissesByDiocese[diocese.id] || [];
                const isLoading = loadingParoisses[diocese.id];
                
                return (
                  <React.Fragment key={diocese.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleDiocese(diocese.id)}
                          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
                          title={isExpanded ? "Masquer les paroisses" : "Afficher les paroisses"}
                        >
                          {isExpanded ? (
                            <FiChevronDown className="w-5 h-5" />
                          ) : (
                            <FiChevronRight className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                        {diocese.diocese}
                        {paroisses.length > 0 && !isExpanded && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({paroisses.length} paroisse{paroisses.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <ConditionalActions
                          entity="dioceses"
                          onEdit={() => {
                            setEditingDiocese(diocese);
                            setShowModal(true);
                            setValue('diocese', diocese.diocese);
                          }}
                          onDelete={async () => {
                            const result = await Swal.fire({
                              title: 'Supprimer ce diocèse ?',
                              text: 'Cette action est irréversible. Toutes les paroisses associées seront également supprimées.',
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonColor: '#d33',
                              cancelButtonColor: '#3085d6',
                              confirmButtonText: 'Oui, supprimer',
                              cancelButtonText: 'Annuler',
                            });
                            if (result.isConfirmed) {
                              try {
                                await diocesesService.delete(diocese.id);
                                fetchDioceses();
                                // Nettoyer le cache des paroisses
                                setParoissesByDiocese(prev => {
                                  const newState = { ...prev };
                                  delete newState[diocese.id];
                                  return newState;
                                });
                                Swal.fire('Supprimé !', 'Le diocèse a été supprimé.', 'success');
                              } catch (error) {
                                Swal.fire('Erreur', error.response?.data?.message || 'Erreur lors de la suppression', 'error');
                              }
                            }
                          }}
                          editIcon={FiEdit}
                          deleteIcon={FiTrash}
                          className="flex space-x-2 justify-end"
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan="3" className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
                              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Chargement des paroisses...</span>
                            </div>
                          ) : paroisses.length === 0 ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                              Aucune paroisse pour ce diocèse
                            </div>
                          ) : (
                            <div className="pl-8">
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Paroisses ({paroisses.length})
                              </h4>
                              <ul className="space-y-1">
                                {paroisses.map((paroisse) => (
                                  <li key={paroisse.id} className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                                    <span className="w-2 h-2 bg-primary-500 dark:bg-primary-400 rounded-full mr-2"></span>
                                    {paroisse.paroisse}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{editingDiocese ? 'Modifier' : 'Ajouter un'} diocèse</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Diocèse *</label>
                <input {...register('diocese', { required: true })} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                {errors.diocese && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" onClick={() => { setShowModal(false); setEditingDiocese(null); reset(); }}>Annuler</button>
                <ConditionalButton
                  entity="dioceses"
                  permission={editingDiocese ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiocesesPage;

